import { useAuth } from '@/providers/AuthProvider';
import { useActiveShift } from '@/hooks/api/useShifts';
import { TicketStatus, UserRole } from '@packages/database/types';

interface PermissionsProps {
  ticketStatus?: TicketStatus;
  currentStatus?: TicketStatus;
  ticket?: any; // TicketDetail
}

export const usePermissions = ({
  ticketStatus,
  currentStatus,
  ticket,
}: PermissionsProps = {}) => {
  const { user } = useAuth();
  const { data: activeShift } = useActiveShift();

  const isAdmin = user?.role === UserRole.ADMIN;
  const isCrew = user?.role === UserRole.CREW;
  const isOnShift = activeShift?.status === 'ACTIVE';

  // =========================================================================
  // ASSIGNMENT STATE — derived from ticket payload
  // =========================================================================

  const activeAssignment =
    ticket?.ticketAssignments?.find((a: any) => a.isActive) ?? null;

  const assignmentStatus = activeAssignment?.assignmentStatus ?? null;

  // My assignee record — must be active (leftAt null)
  const myAssignee =
    activeAssignment?.collaborators?.find(
      (a: any) => a.userId === user?.id && a.leftAt === null,
    ) ?? null;

  const primaryAssignee =
    activeAssignment?.collaborators?.find(
      (a: any) => a.role === 'PRIMARY' && a.leftAt === null,
    ) ?? null;

  const collaborators =
    activeAssignment?.collaborators?.filter(
      (a: any) => a.role === 'COLLABORATOR' && a.leftAt === null,
    ) ?? [];

  const iAmPrimary = myAssignee?.role === 'PRIMARY';
  const iAmCollaborator = myAssignee?.role === 'COLLABORATOR';
  const iAmAssigned = !!myAssignee;
  // Only PRIMARY with canEdit:true can edit — collaborators are read-only
  const iAmEditor = iAmPrimary && myAssignee?.canEdit === true;

  // =========================================================================
  // TERMINAL STATE
  // =========================================================================

  const isTerminal =
    ticketStatus === TicketStatus.COMPLETED ||
    ticketStatus === TicketStatus.CANCELLED ||
    ticketStatus === TicketStatus.FIELD_COMPLETE;

  // =========================================================================
  // EDIT PERMISSION
  // Rule: Admin always (non-terminal) | Crew: on shift + PRIMARY + canEdit
  // =========================================================================

  const canEditTicket = isTerminal
    ? false
    : isAdmin
      ? true
      : isCrew && isOnShift && iAmEditor;

  // =========================================================================
  // COMPLETION
  // Only PRIMARY assignee who is on shift can complete
  // =========================================================================

  const canSubmitCompletion = isAdmin
    ? false // admin has review tab, not completion
    : isCrew && isOnShift && iAmPrimary && !isTerminal;

  // =========================================================================
  // CLAIM — only when on shift, ticket pending, not yet assigned
  // =========================================================================

  const canClaimTicket =
    isCrew && isOnShift && assignmentStatus === 'PENDING_CLAIM' && !iAmAssigned;

  // =========================================================================
  // JOIN — only when on shift, ticket claimed, not yet joined
  // =========================================================================

  const canJoinTicket =
    isCrew && isOnShift && assignmentStatus === 'ASSIGNED' && !iAmAssigned;

  // =========================================================================
  // TRANSFER EDIT — only current editor (PRIMARY) can hand off
  // =========================================================================

  const canTransferEdit =
    isCrew && iAmEditor && assignmentStatus === 'ASSIGNED';

  // =========================================================================
  // BANNER VISIBILITY
  // =========================================================================

  // Not on shift — read-only, no actions at all
  const showOffShiftBanner = isCrew && !isOnShift && !isTerminal;

  // On shift, pending claim
  const showClaimBanner = canClaimTicket;

  // On shift, already claimed by someone else, not yet joined
  const showJoinBanner = canJoinTicket;

  // Joined as collaborator — informational only
  const showCollabBanner = isCrew && iAmCollaborator && !isTerminal;

  // Primary with collaborators — can transfer edit
  const showTransferBanner = isCrew && iAmPrimary && collaborators.length > 0;

  return {
    // Roles
    isAdmin,
    isCrew,
    isOnShift,

    // Assignment state
    activeAssignment,
    assignmentStatus,
    myAssignee,
    primaryAssignee,
    collaborators,
    iAmPrimary,
    iAmCollaborator,
    iAmAssigned,
    iAmEditor,

    // Permissions
    canEditTicket,
    canSubmitCompletion,
    canClaimTicket,
    canJoinTicket,
    canTransferEdit,

    // Banner flags
    showOffShiftBanner,
    showClaimBanner,
    showJoinBanner,
    showCollabBanner,
    showTransferBanner,
  };
};
